import KmxMoreVerticalFilled from '@kmx/mui-icons/KmxMoreVerticalFilled';
import Divider from '@mui/material/Divider';
import IconButton, { type IconButtonProps } from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MuiMenu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { memo, useCallback, useMemo, useState } from 'react';

import type { MenuOptions } from '@/types/MenuOptions';

type MenuProps = Omit<IconButtonProps, 'className' | 'id' | 'onClick'> & {
  options?: MenuOptions;
  stockNumber: string;
};

function Menu({
  options = [],
  size = 'small',
  stockNumber,
  ...iconButtonProps
}: MenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null);

  const buttonId = `kmx-car-tile-menu-button-${stockNumber}`;

  const menuId = `kmx-car-tile-menu-${stockNumber}`;

  const open = Boolean(anchorEl);

  const handleMenuButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const menuItems = useMemo(() => {
    return options.map((option) => {
      if (option.type === 'divider') {
        return <Divider key={option.key} {...option.dividerProps} />;
      }

      const handleMenuItemClick: typeof option.onClick = (event) => {
        option.onClick(event);
        handleClose();
      };

      return (
        <MenuItem
          key={option.key}
          onClick={handleMenuItemClick}
          {...option.menuItemProps}
        >
          {option.icon ? <ListItemIcon>{option.icon}</ListItemIcon> : null}
          <ListItemText>{option.text}</ListItemText>
        </MenuItem>
      );
    });
  }, [handleClose, options]);

  if (!options || options.length === 0) {
    return null;
  }

  return (
    <>
      <IconButton
        aria-controls={open ? menuId : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        aria-label="More actions"
        className="kmx-car-tile__menu-button"
        id={buttonId}
        size={size}
        onClick={handleMenuButtonClick}
        {...iconButtonProps}
      >
        <KmxMoreVerticalFilled />
      </IconButton>
      <MuiMenu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        className="kmx-car-tile__menu"
        id={menuId}
        onClose={handleClose}
        open={open}
        slotProps={{
          list: {
            'aria-labelledby': buttonId,
          },
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {menuItems}
      </MuiMenu>
    </>
  );
}

export default memo(Menu);
